import { create } from 'zustand';
import type { PlayerConfig, VariantId } from '@/engine/types';
import { seatToPlayer, type RemoteSeat } from './protocol';

export type RoomStatus =
  | 'idle'
  /** Host: room open, waiting for guests / start. */
  | 'hosting'
  /** Client: connecting or reconnecting. */
  | 'connecting'
  /** Client: connected, waiting for the host to start. */
  | 'waiting'
  /** Either side: a game is running. */
  | 'playing'
  | 'error'
  /** Host ended the room (client side). */
  | 'ended';

export type RoomRole = 'none' | 'host' | 'client';

/** Health of this device's own link to the relay. */
export type ConnectionStatus = 'connected' | 'reconnecting' | 'lost';

/** Where a joining guest is in the handshake. */
export type JoinStage = 'idle' | 'relay' | 'host' | 'seat' | 'done';

/** Diagnostics for the Connection sheet; every field is best-effort. */
export interface ConnectionDiag {
  relayUrl: string;
  /** Monotonic per-attach counter; stale-socket events are ignored. */
  epoch: number;
  lastInboundAt: number | null;
  lastOutboundAt: number | null;
  /** Round-trip of the last ping, ms. */
  pingMs: number | null;
  reconnects: number;
  /** Most recent transport-level error text, if any. */
  lastError: string | null;
}

export interface RoomState {
  status: RoomStatus;
  role: RoomRole;
  code: string | null;
  hostName: string | null;
  variantId: VariantId | null;
  /** Remote guests. On the client this includes the local player. */
  seats: RemoteSeat[];
  /** Client: our assigned player id. */
  selfPlayerId: string | null;
  error: string | null;
  /** Our own link to the relay. 'lost' = gave up; user must retry. */
  connection: ConnectionStatus;
  /** Client: whether the host is currently attached to the room. */
  hostPresent: boolean;
  /**
   * Short user-facing reason the last action was not sent (e.g. while
   * reconnecting). Cleared by the next successful action. UI shows it as a toast.
   */
  blockedNotice: string | null;
  /** Guest: current handshake stage while joining or rejoining. */
  joinStage: JoinStage;
  /** Guest: human-readable hint for the current stage or its timeout. */
  joinHint: string | null;
  diag: ConnectionDiag;
}

interface RoomStore extends RoomState {
  patch: (p: Partial<RoomState>) => void;
  reset: () => void;
}

const INITIAL: RoomState = {
  status: 'idle',
  role: 'none',
  code: null,
  hostName: null,
  variantId: null,
  seats: [],
  selfPlayerId: null,
  error: null,
  connection: 'connected',
  hostPresent: true,
  blockedNotice: null,
  joinStage: 'idle',
  joinHint: null,
  diag: {
    relayUrl: '',
    epoch: 0,
    lastInboundAt: null,
    lastOutboundAt: null,
    pingMs: null,
    reconnects: 0,
    lastError: null,
  },
};

export const useRoomStore = create<RoomStore>()(set => ({
  ...INITIAL,
  patch: p => set(p),
  reset: () => set(INITIAL),
}));

/**
 * Remote guests as player configs (kind 'remote'). The lobby appends these
 * to the host's local seats when building the GameConfig.
 */
export function getRemoteSeats(): PlayerConfig[] {
  const { role, seats } = useRoomStore.getState();
  if (role !== 'host') return [];
  return seats.map(seatToPlayer);
}

export function isHostingRoom(): boolean {
  const { role, status } = useRoomStore.getState();
  return role === 'host' && (status === 'hosting' || status === 'playing');
}
